import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";

import { toast } from "react-toastify";
import { apiStatuses } from "Definations/Constants";
import { TokenService } from "./TokenService";
import ApiUrls from "Definations/ApiUrls";

interface RetryQueueItem {
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
  config: AxiosRequestConfig;
}

export function httpErrorHandler(error: AxiosError & { config?: any }) {

  if (axios.isAxiosError(error)) {
    const response: any = error.response;
    const request = error.request;

    if (error.code === "ERR_NETWORK") {
      toast.warning("Connection problems..");
    } else if (error.code === "ERR_CANCELED") {
      // toast.error("Connection canceled..");
    }

    if (response) {
      const statusCode = response.status;
      
      if (statusCode === apiStatuses.INTERNAL_SERVER_ERROR) {
        toast.error("Internal Server Error");
      } else if (statusCode === apiStatuses.API_DOES_NOT_EXIST) {
        toast.error(response?.data?.message || "The requested resource does not exist or has been deleted");
      } else if (statusCode === apiStatuses.API_UNAUTHORISED) {
        toast.error(response.data?.message);
      } else if (statusCode === apiStatuses.FORBIDDEN) {
        toast.error(response.data?.message ?? "You don't have permission.");
      } else if (statusCode === apiStatuses.MULTIPLE_RESPONSES) {
        toast.warning(response.data?.message);
      } else if (statusCode === apiStatuses.UNABLE_PROCESS) {
        toast.error("Unable to process.");
      } else if (statusCode === apiStatuses.BAD_REQUEST) {
        const msg = Array.isArray(response.data?.message) ? response.data?.message?.[0] : response.data?.message;
        toast.error(msg);
      }
    } else if (request) {
      // toast.info("The request was made but no response was received");
    }
  }

  return Promise.reject(error);
}

// Create a list to hold the request queue
const refreshAndRetryQueue: RetryQueueItem[] = [];

// Flag to prevent multiple token refresh requests
let isRefreshing = false;

const refreshToken = async (): Promise<string> => {
  const refreshToken = TokenService.getRefreshToken();
  if (!refreshToken) throw new Error("No refresh token available");

  const response = await refreshTokenRequest({ refresh: refreshToken });

  const newAccessToken = response.data?.access;
  if (newAccessToken) {
    TokenService.setAccessToken(newAccessToken);
    return newAccessToken;
  } else {
    throw new Error("Failed to retrieve new access token");
  }
};

function createHttpInstance() {
  const instance = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      "Content-Type": "application/json",
    },
    validateStatus: (status) => {
      return (
        (status >= apiStatuses.API_SUCCESS && status <= apiStatuses.API_DELETE) || status === apiStatuses.TEMP_REQUIRED
      )
    },
    timeout: 300000,
  });

  instance.interceptors.request.use(async (config: any) => {
    const accessToken = TokenService.getAccessToken();
    if (accessToken) {
      config.headers["Authorization"] = `Token ${accessToken}`;
    }
    return config;
  });

  instance.interceptors.response.use(
    (res) => res,
    async (error) => {
      const originalRequest: AxiosRequestConfig = error.config;
    
      if (error.config?.skipInterceptor) return Promise.reject(error);
      if (error.response && error.response.status === apiStatuses.FORBIDDEN) {
        if (!isRefreshing) {
          isRefreshing = true;
          try {
            // Refresh the access token
            const newAccessToken = await refreshToken();
            
            // Update the request headers with the new access token
            error.config.headers['Authorization'] = `Token ${newAccessToken}`;
            
            // Retry all requests in the queue with the new token
            refreshAndRetryQueue.forEach(({ config, resolve, reject }) => {
              instance
                .request(config)
                .then((response) => resolve(response))
                .catch((err) => reject(err));
            });

            // Clear the queue
            refreshAndRetryQueue.length = 0;

            // Retry the original request
            return instance(originalRequest);
          } catch (refreshError) {
            // Handle token refresh error
            httpRequest.post(`${ApiUrls.adminLogout}`);
            TokenService.clearTokens();
            throw refreshError;
          } finally {
            isRefreshing = false;
          }
        }

        // Add the original request to the queue
        return new Promise<void>((resolve, reject) => {
          refreshAndRetryQueue.push({ config: originalRequest, resolve, reject });
        });
      }

      return httpErrorHandler(error);
    }
  );

  return instance;
}

const httpRequest: AxiosInstance = createHttpInstance();

export const refreshTokenRequest = async (data: { refresh: string; }) => {
  return httpRequest.post(`${ApiUrls.tokenRefreshUrl}`, data);
}

export default httpRequest;
